import { useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuthStore } from "@/store/authStore";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RecoveryLogin from "@/pages/RecoveryLogin";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicShare from "@/pages/PublicShare";
import Dashboard from "@/pages/Dashboard";
import TeamMemberOnboarding from "@/pages/TeamMemberOnboarding";
import MyActivities from "@/pages/MyActivities";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import TasksHub from "@/pages/TasksHub";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import PlatformShell from "@/pages/platform/PlatformShell";
import PlatformOverview from "@/pages/platform/PlatformOverview";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformTenantInvitations from "@/pages/platform/PlatformTenantInvitations";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import PlatformOutbox from "@/pages/platform/PlatformOutbox";
import PlatformAnnouncements from "@/pages/platform/PlatformAnnouncements";
import PlatformSystemHealth from "@/pages/platform/PlatformSystemHealth";
import PlatformAuditLog from "@/pages/platform/PlatformAuditLog";
import PlatformDigest from "@/pages/platform/PlatformDigest";
import EmailOutbox from "@/pages/EmailOutbox";
import MagicLogin from "@/pages/MagicLogin";
import NotificationPrefs from "@/pages/NotificationPrefs";
import ReassignTasks from "@/pages/ReassignTasks";
import Integrations from "@/pages/Integrations";
import QuickNotes from "@/pages/QuickNotes";
import Notes from "@/pages/Notes";
import Motivasyon from "@/pages/Motivasyon";
import MotivationRules from "@/pages/MotivationRules";
import Takvim from "@/pages/Takvim";
import ContactsPeople from "@/pages/ContactsPeople";
import ContactsCompanies from "@/pages/ContactsCompanies";
import CopTeams from "@/pages/cop/CopTeams";
import CopDeals from "@/pages/cop/CopDeals";
import CopConstruction from "@/pages/cop/CopConstruction";
import ExpoTemplates from "@/pages/expo/ExpoTemplates";
import ExpoFairs from "@/pages/expo/ExpoFairs";
import ExpoFiles from "@/pages/expo/ExpoFiles";
import ExpoQuestions from "@/pages/expo/ExpoQuestions";
import ExpoFairProjects from "@/pages/expo/ExpoFairProjects";
import PublicExpoForm from "@/pages/PublicExpoForm";
import CopConstructionTracking from "@/pages/cop/CopConstructionTracking";
import CopConstructionTrackingDetail from "@/pages/cop/CopConstructionTrackingDetail";
import CopInvoices from "@/pages/cop/CopInvoices";
import CopFiles from "@/pages/cop/CopFiles";
import CopDashboard from "@/pages/cop/CopDashboard";
import CopHubs from "@/pages/cop/CopHubs";
import OrgChart from "@/pages/OrgChart";
import PublicOrgChart from "@/pages/PublicOrgChart";
import TakipDashboard from "@/pages/TakipDashboard";
import PlatformContactStatuses from "@/pages/platform/PlatformContactStatuses";
import PlatformDealStages from "@/pages/platform/PlatformDealStages";
import PlatformTemplates from "@/pages/platform/PlatformTemplates";
import InstallPrompt from "@/components/pwa/InstallPrompt";
import { useVersionCheck } from "@/lib/useVersionCheck";

function App() {
  const fetchMe = useAuthStore((s) => s.fetchMe);
  useVersionCheck();
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/recovery" element={<RecoveryLogin />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/magic" element={<MagicLogin />} />
          <Route path="/share/:token" element={<PublicShare />} />
          <Route path="/org-chart-share/:token" element={<PublicOrgChart />} />
          <Route path="/expo-form/:token" element={<PublicExpoForm />} />

          <Route
            path="/app/onboarding-welcome"
            element={
              <ProtectedRoute>
                <TeamMemberOnboarding />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="my-activities" element={<MyActivities />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:projectId" element={<ProjectDetail />} />
            <Route path="tasks" element={<TasksHub />} />
            <Route path="quick-notes" element={<QuickNotes />} />
            <Route path="notes" element={<Notes />} />
            <Route path="motivasyon" element={<Motivasyon />} />
            <Route path="motivasyon-kurallari" element={<MotivationRules />} />
            <Route path="takvim" element={<Takvim />} />
            <Route path="contacts/people" element={<ContactsPeople />} />
            <Route path="contacts/companies" element={<ContactsCompanies />} />
            <Route path="cop/teams" element={<CopTeams />} />
            <Route path="cop/dashboard" element={<CopDashboard />} />
            <Route path="cop/deals" element={<CopDeals />} />
            <Route path="cop/construction" element={<CopConstruction />} />
            <Route path="cop/hubs" element={<CopHubs />} />
            <Route path="cop/construction-tracking" element={<CopConstructionTracking />} />
            <Route path="cop/construction-tracking/:id" element={<CopConstructionTrackingDetail />} />
            <Route path="cop/invoices" element={<CopInvoices />} />
            <Route path="cop/files" element={<CopFiles />} />
            <Route path="expo/fairs" element={<ExpoFairs />} />
            <Route path="expo/fair-projects" element={<ExpoFairProjects />} />
            <Route path="expo/files" element={<ExpoFiles />} />
            <Route path="expo/questions" element={<ExpoQuestions />} />
            <Route
              path="expo/templates"
              element={
                <ProtectedRoute roles={["super_admin", "ultra_admin"]}>
                  <ExpoTemplates />
                </ProtectedRoute>
              }
            />
            <Route path="org-chart" element={<OrgChart />} />
            <Route path="takip" element={<TakipDashboard />} />
            <Route
              path="users"
              element={
                <ProtectedRoute roles={["super_admin", "ultra_admin"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={<Settings />}
            />
            <Route
              path="integrations"
              element={
                <ProtectedRoute roles={["super_admin", "ultra_admin"]}>
                  <Integrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="billing"
              element={
                <ProtectedRoute roles={["super_admin", "ultra_admin"]}>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route path="email-outbox" element={<EmailOutbox />} />
            <Route path="notifications" element={<NotificationPrefs />} />
            <Route
              path="reassign"
              element={
                <ProtectedRoute roles={["super_admin", "ultra_admin"]}>
                  <ReassignTasks />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Platform admin has its own shell with a dedicated sidebar */}
          <Route
            path="/app/platform"
            element={
              <ProtectedRoute roles={["platform_admin"]}>
                <PlatformShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformOverview />} />
            <Route path="tenants" element={<PlatformTenants />} />
            <Route path="invitations" element={<PlatformTenantInvitations />} />
            <Route path="users" element={<PlatformUsers />} />
            <Route path="billing" element={<PlatformBilling />} />
            <Route path="outbox" element={<PlatformOutbox />} />
            <Route path="announcements" element={<PlatformAnnouncements />} />
            <Route path="system" element={<PlatformSystemHealth />} />
            <Route path="audit" element={<PlatformAuditLog />} />
            <Route path="digest" element={<PlatformDigest />} />
            <Route path="contact-statuses" element={<PlatformContactStatuses />} />
            <Route path="deal-stages" element={<PlatformDealStages />} />
            <Route path="templates" element={<PlatformTemplates />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <InstallPrompt />
    </div>
  );
}

export default App;

